import { Flex } from "antd"
import { useTranslation } from "react-i18next"

import Button from "../../../../../../../Components/Button"
import InfoTooltip from "../../../../../../../Components/InfoTooltip/InfoTooltip"
import { exclusiveValues, toggleChipValue } from "../../../utils/chipSelection"
import { mergeOptionTooltips } from "../../../utils/optionTooltipMerge"
import { visibleOptions } from "../../../utils/showIf"
import { getValueAt, setValueAt } from "../../../utils/valuePath"
import InlineChipInput from "../chips/InlineChipInput"
import RemovableChip from "../chips/RemovableChip"

import chipStyles from "../../chips.module.less"

// Toggleable option chips, optionally with free-text "+ Other" additions. Those live in
// a sibling `<key>_custom` array so they survive the chip toggle dance, which is why
// this control writes through the raw patch as well as its own value.
const ChipMultiControl = ({
  question,
  value,
  onChange,
  onTouch,
  sectionState,
  onPatch,
  buildingBlocks
}) => {
  const { t } = useTranslation()

  const selected = (Array.isArray(value) ? value : []).filter((v) => v !== "__other__")
  const options = mergeOptionTooltips(visibleOptions(question, buildingBlocks), question.key)
  const customsKey = `${question.key}_custom`
  const customs = getValueAt(sectionState, customsKey) || []
  const exclusive = exclusiveValues(options)
  const writeCustoms = (next) => onPatch(setValueAt(sectionState, customsKey, next))

  const handleToggle = (val) => {
    onTouch()
    onChange(toggleChipValue(selected, val, options))
    // Turning ON an exclusive option also clears any "+ Other" custom chips: the whole
    // point of "None" is that nothing else is answered.
    if (exclusive.includes(val) && !selected.includes(val) && customs.length) writeCustoms([])
  }

  const handleAddCustom = (val) => {
    // Prevent duplicates (case-insensitive).
    if (customs.some((c) => c.toLowerCase() === val.toLowerCase())) return
    writeCustoms([...customs, val])
    // Adding a custom value deselects any exclusive option.
    if (exclusive.some((v) => selected.includes(v)))
      onChange(selected.filter((v) => !exclusive.includes(v)))
    onTouch()
  }

  return (
    <Flex wrap="wrap" gap={8} className={chipStyles.chipRow}>
      {options.map((opt) => {
        const isOn = selected.includes(opt.value)
        return (
          <Button
            key={opt.value}
            type={isOn ? "primary" : "default"}
            className={`${chipStyles.chip} ${isOn ? chipStyles.chipOn : ""}`}
            onClick={() => handleToggle(opt.value)}
            text={
              opt.tooltip ? (
                <span className={chipStyles.chipLabelwrap}>
                  {t(opt.label)}
                  {/* stopPropagation so reading the tooltip never toggles the chip */}
                  <span className={chipStyles.chipTip} onClick={(e) => e.stopPropagation()}>
                    <InfoTooltip title={t(opt.tooltip)} />
                  </span>
                </span>
              ) : (
                t(opt.label)
              )
            }
          />
        )
      })}
      {customs.map((c, i) => (
        <RemovableChip
          key={`c-${i}`}
          label={c}
          // Carried over verbatim: antd 5 injects its .ant-btn base rules globally at
          // runtime, so these are not inert. The text-multi chip never had them, so
          // dropping them here is a visual change, not a cleanup.
          className="ant-btn ant-btn-primary"
          onRemove={() => writeCustoms(customs.filter((_, idx) => idx !== i))}
        />
      ))}
      {question.allowOther ? (
        <InlineChipInput addLabel={t("+ Other")} onCommit={handleAddCustom} />
      ) : null}
    </Flex>
  )
}

export default ChipMultiControl
